import store from '../store';
import { taffy } from 'taffy/lib/taffy.min';

/**
 * List of CL modules to load.
 * Affect Production-Build mode build, only modules specified are included.
 */
const CL_MODULES = [
  'cl.core',
  'cl.mse',
  'cl.dash',
  'cl.hls',
  'cl.smooth',
  'cl.thumbnails',
  'cl.youbora',
  'cl.ima',
  'cl.crypto',
  'cl.onboard',
  'cl.cast',
  'cl.airplay',
  'cl.persistent',
  'cl.htmlcue',
  'cl.ttml',
  'cl.verimatrix',
  'cl.vtt',
  'cl.vr',
  'cl.broadpeak'
];

/**
 * Load dependencies such as analytics libs, etc. also polyfills.
 */
function loadDependencies () {
  if (PRODUCTION) {
    require('../../../lib/dist/release/cjs/polyfill.min');
  }

  // Add transmuxing support for HLS+TS
  window.muxjs = require('../../node_modules/mux.js/dist/mux');
  // Add youboralib
  window.youbora = require('../../node_modules/youboralib/src/youboralib');
  // Add Broadpeak SmartLib base SDK
  window.SmartLib = require('../../node_modules/@broadpeak/smartlib-all-compatibility/base.smartlib').SmartLib;
  // Add Broadpeak SmartLib Analytics SDK
  require('../../node_modules/@broadpeak/smartlib-all-compatibility/analytics.smartlib');

  try {
    // Enable debug logs for Broadpeak SmartLib
    // eslint-disable-next-line no-undef
    LoggerManager.getInstance().setLogLevel(1);
  } catch (e) {
    // ignore
  }
}

/**
 * Load clpp SDK.
 * - In production mode we load the CJS release build of the SDK.
 * - In development mode we load the uncompiled version via Closure Library
 * utils which enables hot reload.
 *
 * @returns {Promise}
 */
export function loadClppSdk () {
  let baseUrl = store.state.scripts.baseUrl;

  // load CSS
  require('../../../lib/src/clpp.styles.css');

  if (PRODUCTION) {
    console.log('Load production build of CLPP SDK');
    let clpp;
    for (let i = 0; i < CL_MODULES.length; i++) {
      const c = require(`../../../lib/dist/release/cjs/${CL_MODULES[i]}`);
      if (i === 0) {
        clpp = c;
      }
    }
    window.clpp = clpp;
    return Promise.resolve();
  }

  console.log('Load development build of CLPP SDK');

  // Make sure that base URL ends with a /
  if (baseUrl.charAt(baseUrl.length - 1) !== '/') {
    baseUrl += '/';
  }

  const p1 = loadScript(baseUrl + 'dist/closure/goog/base.js');
  return p1.then(() => {
    // enable chrome app sage loading. This makes sure that we
    // are not trying to load via writing scripts inline but always
    // load scripts by adding script tags to the header.
    // At least on edge, writing to the body does not seem to trigger
    // script evaluation always
    goog.ENABLE_CHROME_APP_SAFE_SCRIPT_LOADING = false;
    const p2 = loadScript(baseUrl + 'dist/debug/cl.deps.js');
    const p3 = loadScript(baseUrl + 'dist/debug/cl.uncompiled.js');
    return Promise.all([p2, p3]).then(() => {
      console.log('Should have all loaded now, but lets wait a bit');
      return new Promise((resolve) => {
        const check = () => {
          const loader = goog.debugLoader_;
          if (loader.depsToLoad_.length === 0 && loader.loadingDeps_.length === 0 && Object.keys(loader.dependencies_).length > 0) {
            console.log('All Closure Dev Dependencies seem to be loaded!');
            resolve();
          } else {
            setTimeout(check, 50);
          }
        };
        setTimeout(check, 0);
      });
    });
  });
}

function initializeClppModules () {
  clpp.log.setLogLevel(clpp.log.Level.INFO);

  clpp.install(clpp.dash.DashComponent);
  clpp.install(clpp.hls.HlsComponent);
  clpp.install(clpp.crypto.CryptoComponent);
  clpp.install(clpp.smooth.SmoothComponent);
  clpp.install(clpp.onboard.OnboardComponent);
  clpp.install(clpp.persistent.PersistentLicenseComponent);
  clpp.install(clpp.drm.HeaderDrmComponent);
  clpp.install(clpp.verimatrix.VcasComponent);
  clpp.install(clpp.htmlcue.HtmlCueComponent);
  clpp.install(clpp.vtt.VttComponent);
  clpp.install(clpp.ttml.TtmlComponent);

  console.info('Clpp modules installed.');
}

/**
 * Load our SDK and dependencies and initialize them in a basic way.
 */
export async function initializeLibs () {
  try {
    loadDependencies();
    await loadClppSdk();
    console.info('Libraries loaded.');
    initializeClppModules();
    store.commit('scripts/loaded', true);
  } catch (e) {
    console.error('Loading libraries failed!', e);
    store.commit('setError', {
      msg: 'Failed to load libraries!'
    });
  }
}

/**
 * Load a script
 *
 * @param {string} src The URL
 */
function loadScript (src) {
  return new Promise((resolve, reject) => {
    var script = document.createElement('script');
    script.async = false;
    console.log('Loading ', src);
    // Bypass cache
    script.src = src + '?v=' + Date.now();

    script.onload = (e) => {
      resolve();
    };

    script.onerror = (e) => {
      reject(new Error('Failed to load ' + src));
    };

    document.querySelector('head').appendChild(script);
  });
}

/**
 * Convert JSDoc links such to HTML links.
 * See `scripts/tsgen/types.jsdoc` for types.
 *
 * @param {JsDocItem[]} doclets
 * @return {JsDocItem[]}
 */
function convertDocletLinks (doclets) {
  const linkRegex = new RegExp('\\{@link ([^}]+)\\|([^}]+)\\}', 'gm');

  doclets.forEach(doclet => {
    if (linkRegex.test(doclet.description)) {
      doclet.description = doclet.description.replaceAll(linkRegex, (match, group1, group2) => {
        let uri = group1.trim();
        if (uri.length === 0) {
          return match;
        }
        const isSymbol = uri.startsWith('clpp.');
        if (isSymbol) {
          uri = `/docs/api/${uri}`;
        }

        let label = group2?.trim() ?? '';
        if (label.length === 0) {
          label = uri;
        }
        return `<a href="${uri}">${label}</a>`;
      });
    }
  });

  return doclets;
}

/**
 * Convert docs
 *
 * @param {string} json docs as JSON string
 * @return {string} docs as JSON string
 */
function convertDocs (json) {
  let doclets = JSON.parse(json);
  doclets = convertDocletLinks(doclets);
  return JSON.stringify(doclets);
}

/**
 * Loads the documentation DB. We load this from a configured location during development
 * and embed the DB in prod.
 */
export function loadDocumentation () {
  if (store.state.docs.db) {
    console.log('Docs available.');
    return Promise.resolve();
  }

  let docsUrl = store.state.scripts.docsUrl;
  if (PRODUCTION) {
    const trimmedVueAppBaseUrl = process.env.VUE_APP_BASE_URL.replace(/\/$/g, '');
    docsUrl = `${trimmedVueAppBaseUrl}/${process.env.VUE_APP_API_JSON}`;
  }
  if (!docsUrl) {
    return Promise.resolve();
  }

  console.log('Docs NOT available. Loading...');
  return new Promise((resolve, reject) => {
    const r = new XMLHttpRequest();
    r.onload = (e) => {
      if (r.status === 200) {
        const data = convertDocs(r.responseText);
        const db = taffy(data);
        window.db = db;
        store.commit('docs/setDb', db);
        resolve();
      } else {
        reject();
      }
    };
    r.open('GET', docsUrl);
    r.send();
  });
}
